import { FC, useEffect, useRef, useState, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { FaCaretDown, FaCaretUp, FaHistory, FaPrint } from 'react-icons/fa';
import { CellProps, Column } from 'react-table';
import useWindowWidth from 'utils/helpers/useWindowWidth';
import styled from 'styled-components';

import TooltipWrapper from 'components/TooltipWrapper';
import Button from 'components/Button';
import { buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { useTranslation } from 'react-i18next';
import { useModal } from 'store/modal';
import { formatDate } from 'utils/helpers/formatters';
import FetchTable from 'components/table/FetchTable';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import Spinner from 'components/Spinner';
import { IReport } from '../types';

interface IProps {
  reportParams: any;
  expandAllRows: boolean;
  setExpandAllRows: (value: boolean) => void;
  setShowDownloadBtn: () => void;
  isDownloadMode: boolean;
}

const DocsTable: FC<IProps> = ({
  reportParams,
  expandAllRows,
  setExpandAllRows,
  setShowDownloadBtn,
  isDownloadMode,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [link, setLink] = useState<string | null>(null);
  const openLinkBtn = useRef<HTMLAnchorElement>(null);
  const { client } = useActiveClientContext();
  const { showModal } = useModal();
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const windowWidth = useWindowWidth();

  useEffect(() => {
    if (link) openLinkBtn?.current?.click();
  }, [link]);

  const renderActions = (documentId: number) => {
    return (
      <>
        <div className="d-inline-block ml-2">
          <TooltipWrapper
            id="tooltip-print"
            overlay={t('documents.table.preview')}
          >
            <Button
              type="button"
              btnType="dark"
              size="sm"
              disabled={isLoadingDownload}
              outlined
              onClick={(e: any) => {
                handlePreviewDoc({
                  showModal,
                  id: documentId,
                  callback: (res: any) => {
                    setLink(res.data);
                  },
                  setIsLoading: setIsLoadingDownload,
                });
                e.stopPropagation();
              }}
            >
              {!isLoadingDownload && <FaPrint size={14} />}

              {isLoadingDownload && <Spinner size={14} />}
            </Button>
          </TooltipWrapper>
        </div>
        <div className="d-inline-block ml-2">
          <TooltipWrapper
            id="tooltip-history"
            overlay={t('documents.table.history')}
          >
            <Button
              type="button"
              btnType="dark"
              size="sm"
              outlined
              onClick={(e: any) => {
                history.push({
                  pathname: buildClientUrl(`/document-activity/${documentId}`, client),
                  state: {
                    fromReports: true,
                  },
                });
                e.stopPropagation();
              }}
            >
              <FaHistory size={14} />
            </Button>
          </TooltipWrapper>
        </div>
        {link && (
          <Link
            ref={openLinkBtn}
            to={{
              pathname: link,
            }}
            target="_blank"
            style={{ display: 'none' }}
          />
        )}
      </>
    );
  };

  // Build columns based on work mode and tab
  const columns: Array<Column<IReport>> = useMemo(
    () => [
      {
        // Build our expander column
        Header: () => <Heading>#</Heading>,
        id: 'expander', // Make sure it has an ID
        disableSortBy: true,
        width: windowWidth > 1600 ? '8%' : '15%',
        Cell: ({ row }: CellProps<IReport>) => (
          // Use Cell to render an expander for each row.
          // We can use the getToggleRowExpandedProps prop-getter
          // to build the expander.
          <>
            <span {...row.getToggleRowExpandedProps()}>
              {row.isExpanded ? (
                <FaCaretUp
                  size={16}
                  color="#32bfa3"
                />
              ) : (
                <FaCaretDown size={16} />
              )}
              <span className="ml-2">{row.original.number}</span>
            </span>
          </>
        ),
      },
      {
        Header: 'ID',
        accessor: 'id',
      },
      {
        Header: t('documents.table.name') || '',
        accessor: 'name',
      },
      {
        Header: t('documents.table.category') || '',
        accessor: 'category',
      },
      {
        Header: t('documents.table.author') || '',
        accessor: 'creator',
      },
      {
        Header: t('documents.table.employee') || '',
        accessor: 'holder',
      },
      {
        Header: t('documents.table.group') || '',
        accessor: 'group',
      },
      {
        Header: t('documents.table.createdAt') || '',
        accessor: 'created_at',
        width: windowWidth < 1600 ? '15%' : '12%',
        Cell: ({ value }: CellProps<IReport>) => <>{formatDate(new Date(value), 'dd.mm.YYY hh:ii:ss')}</>,
      },
      {
        Header: () => <div style={{ textAlign: 'right' }}>{t('table.actions')}</div>,
        accessor: 'actions',
        width: windowWidth < 1600 ? '10%' : '8%',
        Cell: ({ row }: CellProps<IReport>) => (
          <div style={{ textAlign: 'right' }}>{renderActions(row.original.id)}</div>
        ),
      },
    ],
    [windowWidth, t, isLoadingDownload, link, client, history, showModal]
  );

  return (
    <FetchTable<IReport>
      columns={columns}
      entityName="documents-report"
      entityEndpoint="reports/documents/generate"
      searchParams={reportParams}
      pageLimit={20}
      skipResetExpanded
      hideFilterBar
      isReport
      showDownloadButton={setShowDownloadBtn}
      expandAllRows={expandAllRows}
      setExpandAllRows={setExpandAllRows}
      isExpandable
      pagination
      isSelectable
      customSelectableRowProps={isDownloadMode ? ['CAN_MULTI_DOWNLOAD'] : undefined}
    />
  );
};

export default DocsTable;

const Heading = styled.div`
  margin-left: 40px;
`;
